import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ShoppingCart, Plus, Minus, Trash2, CreditCard, Package, User, Sparkles, X, Search, Star, ChevronLeft, ChevronRight, Layers, UtensilsCrossed } from 'lucide-react';
import { toast } from 'sonner';
import FnBPrintDialog from './FnBPrintDialog';
import FnBPaymentDialog from './FnBPaymentDialog';
import CustomizeIngredientsDialog from './CustomizeIngredientsDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StandaloneFnBOrder() {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [customerName, setCustomerName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [completedOrder, setCompletedOrder] = useState(null);
  const [unpaidOrders, setUnpaidOrders] = useState([]);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [customizingProduct, setCustomizingProduct] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  
  // New states for search, filter, and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchProducts();
    fetchUnpaidOrders();
    
    const interval = setInterval(fetchUnpaidOrders, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchProducts = async () => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/products`, { headers });
      const productsData = response.data?.products || response.data || [];
      setProducts(Array.isArray(productsData) ? productsData : []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setError(error.message || 'Gagal memuat produk');
      toast.error('Gagal memuat produk F&B');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnpaidOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/orders/fnb`, { headers });
      const orders = response.data || [];
      setUnpaidOrders(orders.filter(o => o.payment_status === 'unpaid'));
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const handlePayment = (order) => {
    setSelectedOrder(order);
    setShowPaymentDialog(true);
  };

  const handlePaymentSuccess = async (orderId, paymentMethod, amountReceived) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await axios.post(
        `${API}/orders/fnb/${orderId}/payment`,
        { 
          payment_method: paymentMethod, 
          amount_received: amountReceived 
        },
        { headers }
      );
      
      toast.success('Pembayaran berhasil!');
      
      setCompletedOrder(response.data.order);
      setShowPrintDialog(true);
      
      fetchUnpaidOrders();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Gagal memproses pembayaran');
      throw error;
    }
  };

  const handleDeleteOrder = (order) => {
    setOrderToDelete(order);
    setShowDeleteDialog(true);
  };

  const confirmDeleteOrder = async () => {
    if (!orderToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      await axios.delete(`${API}/orders/fnb/${orderToDelete.id}`, { headers });
      
      toast.success('Order berhasil dihapus');
      setShowDeleteDialog(false);
      setOrderToDelete(null);
      fetchUnpaidOrders();
    } catch (error) {
      console.error('Delete order error:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus order');
    }
  };

  const addToCart = (product, customRecipe = null, customPrice = null) => {
    // For composite products, add with default or custom recipe
    if (product.type === 'composite') {
      const cartItem = {
        ...product,
        quantity: 1,
        custom_recipe: customRecipe || product.recipe || [],
        price: customPrice || product.price, // Use custom price if available
        id: customRecipe ? `${product.id}_${Date.now()}` : product.id // Unique ID for custom items
      };
      setCart([...cart, cartItem]);
      toast.success(`${product.name} ditambahkan ${customRecipe ? '(custom)' : '(standar)'}`);
      return;
    }
    
    // For simple products
    const existing = cart.find(item => item.id === product.id);
    if (existing) {
      setCart(cart.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    toast.success(`${product.name} ditambahkan`);
  };

  const handleCustomizeProduct = (product) => {
    setCustomizingProduct(product);
  };

  const handleCustomizeConfirm = (customizedProduct) => {
    addToCart(customizedProduct, customizedProduct.custom_recipe, customizedProduct.custom_price);
    setCustomizingProduct(null);
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.id === productId) {
        const newQty = item.quantity + delta;
        return newQty > 0 ? { ...item, quantity: newQty } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
    toast.info('Item dihapus dari keranjang');
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const handleToggleFavorite = async (productId, e) => {
    e.stopPropagation();
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API}/products/${productId}/favorite`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        fetchProducts(); // Refresh products
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error('Gagal mengubah status favorit');
    }
  };

  // Filter and paginate products
  // Dynamically get unique categories from products (case-insensitive, max 5 total including 'all' and 'favorite')
  const categoryMap = new Map();
  products.forEach(p => {
    if (p.category) {
      const normalized = p.category.toLowerCase();
      if (!categoryMap.has(normalized)) {
        // Store the first occurrence with proper capitalization
        categoryMap.set(normalized, p.category.charAt(0).toUpperCase() + p.category.slice(1).toLowerCase());
      }
    }
  });
  const uniqueProductCategories = Array.from(categoryMap.values());
  const categories = ['all', 'favorite', ...uniqueProductCategories.slice(0, 3)];
  
  const filteredProducts = products.filter(product => {
    const matchSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchCategory = selectedCategory === 'all' 
      || (selectedCategory === 'favorite' && product.is_favorite)
      || (product.category && product.category.toLowerCase() === selectedCategory.toLowerCase());
    return matchSearch && matchCategory;
  }).sort((a, b) => {
    // Sort favorites first
    if (a.is_favorite && !b.is_favorite) return -1;
    if (!a.is_favorite && b.is_favorite) return 1;
    return 0;
  });
  
  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + itemsPerPage);
  
  // Reset page when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  const [submitting, setSubmitting] = useState(false);

  const handleCheckout = async () => {
    if (!customerName.trim()) {
      toast.error('Nama customer wajib diisi');
      return;
    }

    if (cart.length === 0) {
      toast.error('Keranjang masih kosong');
      return;
    }

    if (submitting) {
      return; // Prevent double submission
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      const orderData = {
        customer_name: customerName,
        items: cart.map(item => ({
          product_id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          category: item.category || 'F&B'
        })),
        total: calculateTotal(),
        payment_method: 'tunai',
        branch_id: user.branch_id,
        type: 'standalone_fnb',
        payment_status: 'unpaid'
      };
      
      await axios.post(`${API}/orders/fnb`, orderData, { headers });
      
      toast.success('Order F&B berhasil disimpan! Silakan lakukan pembayaran.');
      
      setCart([]);
      setCustomerName('');
      
      fetchUnpaidOrders();
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Gagal menyimpan order');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-400 mb-4"></div>
        <p className="text-white font-medium">Loading produk F&B...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gradient-to-br from-red-500/20 to-red-600/20 border-2 border-red-500/50 rounded-2xl p-8 text-center">
        <p className="text-white font-bold text-lg mb-2">Error loading F&B products</p>
        <p className="text-white/80 text-sm mb-4">{error}</p>
        <button
          onClick={fetchProducts}
          className="bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white px-6 py-2 rounded-lg font-semibold transition-all shadow-lg"
        >
          Coba Lagi
        </button>
      </div>
    );
  }

  return (
    <div className="billing-fnb-workspace grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_380px] gap-5 items-start">
      {/* Products Grid */}
      <div className="fnb-catalog min-w-0">
        {/* Modern Header with gradient */}
        <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 rounded-3xl p-6 mb-6 shadow-2xl shadow-orange-500/30">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-black text-white mb-1 flex items-center gap-2">
                <UtensilsCrossed className="h-6 w-6" />
                Kasir F&B
              </h3>
              <p className="text-white/90 text-sm font-medium">{products.length} produk lezat tersedia</p>
            </div>
            <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-2xl border-2 border-white/30">
              <p className="text-white font-black text-lg">{cart.length}</p>
              <p className="text-white/80 text-xs font-medium">Di keranjang</p>
            </div>
          </div>
        </div>
        
        {/* Search Bar & Category Filter - Combined in one card */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-4 mb-6 border border-slate-700">
          {/* Search Bar */}
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Cari menu... (Chitato, Indomie, dll)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-700 border-2 border-slate-600 rounded-xl text-white placeholder-slate-400 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-500/50"
            />
          </div>
          
          {/* Category Filter Chips - Max 5 categories */}
          <div className="flex gap-2 flex-wrap">
            {categories.slice(0, 5).map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition-all ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {category === 'all' ? ' Semua' : category === 'favorite' ? '⭐ Favorit' : ` ${category}`}
              </button>
            ))}
          </div>
        </div>
        
        {products.length === 0 ? (
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 border-2 border-slate-700 rounded-3xl p-16 text-center">
            <div className="bg-orange-500/10 rounded-full p-6 w-fit mx-auto mb-4">
              <Package className="h-20 w-20 text-orange-400" />
            </div>
            <p className="text-white/80 font-bold text-lg mb-2">Belum Ada Menu</p>
            <p className="text-white/50 text-sm">Hubungi admin untuk menambahkan produk F&B</p>
          </div>
        ) : (
          <div className="fnb-product-grid grid grid-cols-2 md:grid-cols-3 2xl:grid-cols-4 gap-3">
            {paginatedProducts.map((product) => {
              const cartItem = cart.find(item => item.id === product.id);
              const inCart = !!cartItem;
              
              return (
                <div
                  key={product.id}
                  className="group relative bg-gradient-to-br from-white to-slate-50 rounded-2xl p-4 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-orange-500/30 border-2 border-slate-200 hover:border-orange-400 overflow-visible"
                >
                  {/* Quantity Badge - floating */}
                  {inCart && (
                    <div className="absolute -top-2 -right-2 z-10">
                      <div className="bg-gradient-to-br from-orange-500 to-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center font-black text-sm shadow-lg shadow-orange-500/50 animate-bounce">
                        {cartItem.quantity}
                      </div>
                    </div>
                  )}
                  
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-400/0 via-pink-400/0 to-red-400/0 group-hover:from-orange-400/10 group-hover:via-pink-400/10 group-hover:to-red-400/10 transition-all duration-500 rounded-2xl"></div>
                  
                  <div className="relative text-center">
                    {/* Product Image or Icon */}
                    <div className="mb-2">
                      {product.image_url ? (
                        <div className="w-20 h-20 mx-auto rounded-xl overflow-hidden border-2 border-orange-200 transform group-hover:scale-110 transition-all duration-300">
                          <img
                            src={`${BACKEND_URL}${product.image_url}`}
                            alt={product.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="bg-gradient-to-br from-orange-100 to-pink-100 rounded-xl p-2 inline-block transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                          <span className="text-4xl">{product.category === 'Makanan' ? '' : ''}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Product Name - Centered with Star on top right */}
                    <div className="relative mb-2">
                      {/* Favorite Star - absolute positioned */}
                      <button
                        onClick={(e) => handleToggleFavorite(product.id, e)}
                        className="absolute -top-1 -right-1 p-1 bg-slate-100 hover:bg-slate-200 rounded-full transition-all z-10"
                      >
                        <Star 
                          className={`h-3.5 w-3.5 ${
                            product.is_favorite 
                              ? 'fill-yellow-400 text-yellow-400' 
                              : 'text-slate-400 hover:text-yellow-400'
                          }`}
                        />
                      </button>
                      
                      {/* Product Name - Centered */}
                      <h4 className="font-bold text-slate-800 text-sm line-clamp-2 min-h-[2.5rem] leading-tight text-center px-6">
                        {product.name}
                      </h4>
                    </div>
                    
                    {/* Compact Price Tag */}
                    <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl px-3 py-1.5 mb-2 shadow-md shadow-orange-500/20">
                      <p className="text-white font-black text-base">
                        Rp {product.price.toLocaleString('id-ID')}
                      </p>
                    </div>
                    
                    {/* Stock Badge or Ingredients Info */}
                    {product.type === 'composite' && product.recipe && product.recipe.length > 0 ? (
                      <div className="px-2 py-1.5 rounded-lg mb-2 bg-blue-50 border border-blue-200">
                        <p className="text-xs font-bold text-blue-700 mb-1"> Komposit</p>
                        <div className="space-y-0.5 text-left">
                          {product.recipe.slice(0, 3).map((ing, idx) => {
                            // Display infinity icon for non-stock ingredients
                            if (ing.is_non_stock) {
                              return (
                                <div key={idx} className="flex items-center justify-between text-[10px]">
                                  <span className="text-gray-600 truncate flex-1 mr-1">
                                    • {ing.name}
                                  </span>
                                  <span className="text-blue-600 font-bold ml-1 flex-shrink-0">
                                    
                                  </span>
                                </div>
                              );
                            }
                            
                            const isLowStock = ing.stock < 10;
                            const isOutOfStock = ing.stock <= 0;
                            return (
                              <div key={idx} className="flex items-center justify-between text-[10px]">
                                <span className="text-gray-600 truncate flex-1 mr-1">
                                  • {ing.name}
                                </span>
                                <span className={`font-bold ml-1 flex-shrink-0 ${
                                  isOutOfStock ? 'text-red-600' : 
                                  isLowStock ? 'text-amber-600' : 
                                  'text-green-600'
                                }`}>
                                  {isOutOfStock ? '' : isLowStock ? '' : ''}
                                </span>
                              </div>
                            );
                          })}
                          {product.recipe.length > 3 && (
                            <p className="text-[10px] text-gray-500 text-center">+{product.recipe.length - 3} lagi</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className={`px-2 py-1 rounded-lg mb-2 text-center ${
                        product.stock > 10 ? 'bg-green-100' : 
                        product.stock > 0 ? 'bg-yellow-100' : 
                        'bg-red-100'
                      }`}>
                        <p className={`text-xs font-bold ${
                          product.stock > 10 ? 'text-green-700' : 
                          product.stock > 0 ? 'text-yellow-700' : 
                          'text-red-700'
                        }`}>
                           Stok: {product.stock || 0}
                        </p>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    {product.type === 'composite' ? (
                      <div className="grid grid-cols-2 gap-2">
                        {/* Add Standard Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            addToCart(product);
                          }}
                          className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 rounded-lg px-2 py-1.5 transition-all shadow-sm hover:shadow-lg"
                        >
                          <div className="flex items-center justify-center gap-1">
                            <Plus className="h-3.5 w-3.5 text-white" />
                            <span className="text-white text-xs font-bold">Add</span>
                          </div>
                        </button>
                        
                        {/* Customize Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCustomizeProduct(product);
                          }}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-lg px-2 py-1.5 transition-all shadow-sm hover:shadow-lg"
                        >
                          <div className="flex items-center justify-center gap-1">
                            <Layers className="h-3.5 w-3.5 text-white" />
                            <span className="text-white text-xs font-bold">Custom</span>
                          </div>
                        </button>
                      </div>
                    ) : (
                      /* Simple product - single Add button */
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          addToCart(product);
                        }}
                        className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 rounded-lg px-3 py-1.5 transition-all shadow-sm hover:shadow-lg"
                      >
                        <div className="flex items-center justify-center gap-1">
                          <Plus className="h-3.5 w-3.5 text-white" />
                          <span className="text-white text-xs font-bold">Tambah</span>
                        </div>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Pagination Controls */}
        {filteredProducts.length > itemsPerPage && (
            <div className="fnb-pagination mt-6 flex items-center justify-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:opacity-50 text-white rounded-lg transition-all disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              
              <div className="px-4 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm font-semibold text-slate-200">
                Halaman {currentPage} dari {totalPages}
              </div>
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="p-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:opacity-50 text-white rounded-lg transition-all disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
      </div>

      {/* Right Sidebar - Split into 2 clear sections */}
      <div className="fnb-sidebar space-y-4 xl:sticky xl:top-3">
        {/* SECTION 1: NEW ORDER (Keranjang) - Fixed height untuk presisi */}
        <div className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-3xl p-6 shadow-2xl shadow-blue-500/30 border-2 border-blue-400/30 min-h-[600px] flex flex-col">
          {/* Header dengan visual yang jelas */}
          <div className="mb-5 pb-5 border-b-2 border-white/20">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/20 rounded-2xl backdrop-blur-sm">
                  <ShoppingCart className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-black text-white">Order Baru</h3>
                  <p className="text-white/70 text-xs font-medium">Buat pesanan customer</p>
                </div>
              </div>
              <div className="bg-white/20 backdrop-blur-sm px-3 py-2 rounded-xl">
                <p className="text-white font-black text-lg">{cart.length}</p>
                <p className="text-white/70 text-xs">item</p>
              </div>
            </div>
            
            {cart.length > 0 && (
              <button
                onClick={() => setCart([])}
                className="w-full bg-red-500/20 hover:bg-red-500/30 border border-red-400/40 text-red-200 hover:text-white text-xs font-bold py-2 px-3 rounded-xl transition-all"
              >
                <X className="h-3 w-3 inline mr-1" />
                Kosongkan Keranjang
              </button>
            )}
          </div>
          
          {/* Customer Name Input */}
          <div className="mb-5">
            <label className="text-white text-xs font-bold mb-2 block flex items-center gap-1.5">
              <User className="h-3.5 w-3.5 text-blue-400" />
              Nama Customer
            </label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="Masukkan nama customer"
              className="w-full px-4 py-2.5 bg-gradient-to-br from-slate-900/90 to-slate-800/90 border-2 border-slate-600/50 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all font-medium text-sm shadow-lg"
            />
          </div>

          {/* Cart Items - flex-grow untuk mengisi ruang */}
          <div className="flex-grow flex flex-col mb-5">
            <div className="flex-grow space-y-2 max-h-80 overflow-y-auto pr-1">
              {cart.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <ShoppingCart className="h-12 w-12 text-white/20 mx-auto mb-3" />
                    <p className="text-white/60 text-sm font-medium">Keranjang kosong</p>
                  </div>
                </div>
              ) : (
                cart.map((item) => (
                <div
                  key={item.id}
                  className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 border border-slate-700/50 rounded-xl p-3 hover:border-blue-500/50 transition-all"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="text-white font-bold text-sm mb-1">{item.name}</p>
                      <p className="text-orange-300 text-xs font-semibold">
                        Rp {item.price.toLocaleString('id-ID')} × {item.quantity}
                      </p>
                    </div>
                    <p className="text-blue-300 font-black text-sm">
                      Rp {(item.price * item.quantity).toLocaleString('id-ID')}
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateQuantity(item.id, -1)}
                      className="p-1.5 bg-red-500/20 hover:bg-red-500/40 border border-red-500/40 rounded-lg text-red-300 transition-all"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="text-white font-black text-sm w-8 text-center bg-slate-700/50 rounded-lg py-1">
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.id, 1)}
                      className="p-1.5 bg-green-500/20 hover:bg-green-500/40 border border-green-500/40 rounded-lg text-green-300 transition-all"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => removeFromCart(item.id)}
                      className="p-1.5 bg-red-500/20 hover:bg-red-500/40 border border-red-500/40 rounded-lg text-red-300 transition-all ml-auto"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Total di bagian bawah cart items */}
          <div className="border-t-2 border-slate-700 pt-4 mb-5">
            <div className="flex justify-between items-center bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/40 rounded-xl p-3">
              <span className="text-white font-bold text-sm">Total:</span>
              <span className="text-2xl font-black text-transparent bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text">
                Rp {calculateTotal().toLocaleString('id-ID')}
              </span>
            </div>
          </div>

          {/* BIG Action Button */}
          <button
            onClick={handleCheckout}
            disabled={cart.length === 0 || !customerName.trim() || submitting}
            className="w-full bg-gradient-to-r from-green-500 via-emerald-500 to-green-600 hover:from-green-600 hover:via-emerald-600 hover:to-green-700 disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed text-white font-black py-4 px-6 rounded-2xl flex items-center justify-center gap-3 transition-all shadow-xl hover:shadow-2xl hover:shadow-green-500/50 hover:scale-105 disabled:hover:scale-100 text-lg"
          >
            <Sparkles className="h-6 w-6" />
            {submitting ? 'Memproses...' : cart.length === 0 ? 'Pilih Item Dulu' : `Buat Order (${cart.length} item)`}
          </button>
          
          <p className="text-white/50 text-xs text-center mt-3 font-medium">
            Order akan disimpan dan siap untuk dibayar
          </p>
        </div>
        
        {/* SECTION 2: UNPAID ORDERS (Tagihan Menunggu Pembayaran) */}
        {unpaidOrders.length > 0 && (
          <div className="bg-gradient-to-br from-orange-600 via-red-600 to-pink-600 rounded-3xl p-6 shadow-2xl shadow-orange-500/30 border-2 border-orange-400/30">
            {/* Header */}
            <div className="mb-5 pb-5 border-b-2 border-white/20">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-white/20 rounded-2xl backdrop-blur-sm">
                    <CreditCard className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-white">Menunggu Bayar</h3>
                    <p className="text-white/70 text-xs font-medium">Order yang belum dibayar</p>
                  </div>
                </div>
                <div className="bg-white/20 backdrop-blur-sm px-3 py-2 rounded-xl">
                  <p className="text-white font-black text-lg">{unpaidOrders.length}</p>
                  <p className="text-white/70 text-xs">order</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2 max-h-64 overflow-y-auto pr-1 scrollbar-hide">
              {unpaidOrders.map((order) => (
                  <div key={order.id} className="bg-gradient-to-br from-orange-900/30 to-orange-800/30 border border-orange-500/40 rounded-xl p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <p className="text-white font-bold text-sm">{order.customer_name}</p>
                        <p className="text-white/60 text-xs">{order.items.length} items</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-orange-300 font-black text-sm">
                          Rp {order.total.toLocaleString('id-ID')}
                        </p>
                        <button
                          onClick={() => handleDeleteOrder(order)}
                          className="p-1 bg-red-500/20 hover:bg-red-500/40 border border-red-500/40 rounded-lg text-red-400 hover:text-red-300 transition-all"
                          title="Hapus order"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    <button
                      onClick={() => handlePayment(order)}
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white text-xs font-bold py-2 px-3 rounded-lg transition-all shadow-lg hover:shadow-blue-500/50 flex items-center justify-center gap-1.5"
                    >
                      <CreditCard className="h-3.5 w-3.5" />
                      Bayar Sekarang
                    </button>
                  </div>
              ))}
            </div>
            
            <p className="text-white/50 text-xs mt-3 text-center">
               Lihat riwayat transaksi di tab "Riwayat"
            </p>
          </div>
        )}
      </div>

      {/* Payment Dialog */}
      <FnBPaymentDialog
        order={selectedOrder}
        open={showPaymentDialog}
        onClose={() => {
          setShowPaymentDialog(false);
          setSelectedOrder(null);
        }}
        onSuccess={handlePaymentSuccess}
      />

      {/* Print Dialog */}
      <FnBPrintDialog
        order={completedOrder}
        open={showPrintDialog}
        onClose={() => {
          setShowPrintDialog(false);
          setCompletedOrder(null);
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <AlertDialogTitle className="text-xl">Hapus Order?</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-base text-gray-600 pt-2">
              Apakah Anda yakin ingin menghapus order dari <span className="font-bold">{orderToDelete?.customer_name}</span>? 
              Order dengan total <span className="font-bold">Rp {orderToDelete?.total.toLocaleString('id-ID')}</span> ini akan dihapus secara permanen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel onClick={() => setShowDeleteDialog(false)} className="bg-gray-100 hover:bg-gray-200">
              Batal
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDeleteOrder}
              className="bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white"
            >
              Hapus Order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Customize Ingredients Dialog */}
      {customizingProduct && (
        <CustomizeIngredientsDialog
          product={customizingProduct}
          onClose={() => setCustomizingProduct(null)}
          onConfirm={handleCustomizeConfirm}
        />
      )}
    </div>
  </div>
  );
}
